/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {MouseEventHandler, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {CTCEntity, Entity} from '@ion/core/src/types/entities';
import Chip from '@ion/dls/src/components/chip/Chip';
import {PrimaryChip} from '@ion/dls/src/components/chip/PrimaryChip';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {FilterChips} from '../types/editors';

export interface SelectedEntityChips {
  selected: CTCEntity[];
  areTotalRowsSelected?: boolean;
  includeSubAccounts?: boolean;
  onDelete?: Function;
  accountName?: string;
  entityType?: Entity;
  filters?: FilterChips;
  noTextTransform?: boolean;
  variant?: Variant;
  activeScroll?: boolean;
  minChips?: number;
}

export enum Variant {
  DEFAULT_GRAY,
  PRIMARY_BLUE
}

interface SelectedEntityChipProps {
  label: string;
  icon?: JSX.Element;
  onDelete?: MouseEventHandler;
  id?: number | string;
  noTextTransform?: boolean;
  variant?: Variant;
  showFullLabel?: boolean;
}

const useStyles = makeStyles(() => ({
  chipContainer : {
    display   : 'flex',
    flexWrap  : 'wrap',
    minHeight : '32px',
    maxHeight : '80px',
    overflowY : props => props?.activeScroll ? 'scroll' : 'auto',
    gap       : props => props?.addGap && '8px 8px'
  },
  chipWrapper : {
    marginBottom : '10px',
    position     : 'relative'
  }
}));

/**
 * Chip displaying the given entity's label.
 */
const SelectedEntityChip = ({
  label,
  icon,
  id,
  onDelete,
  noTextTransform,
  showFullLabel,
  variant,
  ...props
}: SelectedEntityChipProps) => {
  const classes = useStyles();
  if (variant === Variant.PRIMARY_BLUE) {
    return (
      <PrimaryChip showFullLabel={showFullLabel} label={label} onDelete={onDelete} />
    );
  }
  return (
    <div key={id} className={classes.chipWrapper}>
      <Chip label={label}
        size='small'
        borderRadius='56px'
        labelColor={dlsColor('grey2')}
        customDeleteIcon={icon}
        onDelete={onDelete}
        noTextTransform={noTextTransform}
        hoverDefault
        showCustomDeleteIcon
        {...props} />
    </div>
  );
};

/**
 * An editor's container displaying the selected entities' names.
 *
 * ```typescript
 *  import {SelectedEntityChips} from '@ion/dls/src/editors/';
 *  <SelectedEntityChips selected={selectedEntities}
 *    accountName={accountName}
 *    areTotalRowsSelected={areTotalRowsSelected}
 *    onDelete={onChipDelete} />
 * ```
 */
export const SelectedEntityChips = ({
  selected,
  areTotalRowsSelected,
  onDelete,
  filters,
  accountName,
  noTextTransform,
  entityType = Entity.device,
  variant = Variant.DEFAULT_GRAY,
  activeScroll = false,
  includeSubAccounts = false,
  minChips = 1
}: SelectedEntityChips) => {
  const classes            = useStyles({addGap: variant === Variant.PRIMARY_BLUE, activeScroll});
  const onDeleteEntityChip = useCallback((selectedEntity: CTCEntity) => () => onDelete(selectedEntity), [onDelete]);

  if (!areTotalRowsSelected) {
    return (
      <div className={classes.chipContainer}>
        {selected?.length > minChips && selected.map((selected, index) => (
          <SelectedEntityChip key={`entity-chip-${index}`}
            id={selected?.id}
            variant={variant}
            noTextTransform={noTextTransform}
            label={selected?.name || selected?.id?.toString()}
            icon={<MaterialExitCloseIcon size={IconSize.small} />}
            onDelete={onDeleteEntityChip(selected)}/>
        ))}
        {selected?.length <= minChips && selected.map((selected) => (
          <SelectedEntityChip label={selected.name || selected?.id?.toString()}
            variant={variant} />
        ))}
      </div>
    );
  }
  return (
    <div className={classes.chipContainer}>
      <SelectedEntityChip showFullLabel
        variant={variant}
        label={translate('grids:selectAll.accountMessage', {
          pluralEntityLabel : translate(`common:entityLabels.${entityType}s`),
          accountName
        })} />
      {includeSubAccounts && (
        <SelectedEntityChip showFullLabel
          variant={variant}
          label={translate('grids:includeSubAccounts')} />
      )}
      {filters?.map(({label}) => (
        <SelectedEntityChip showFullLabel variant={variant} label={label} />
      ))}
    </div>
  );
};

export default SelectedEntityChips;
